export interface PageSEOConfig {
  title: string;
  description: string;
  schema?: Record<string, any> | Array<Record<string, any>>;
}

export const SEO_DATA: Record<string, PageSEOConfig> = {
  home: {
    title: "25th & Staffing — Retained Executive Search, Rooted in Africa",
    description:
      "Connecting you to talents across the globe. Human-led, AI-powered retained search uncovering Africa's brightest talent for world-class teams anywhere.",
  },
  executiveSearch: {
    title: "Executive Search Solutions — 25th & Staffing",
    description:
      "Executive search solutions rooted in results. Adaptive, research-driven leadership recruitment to find and hire the perfect executive talent for your team.",
    schema: {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: "25th & Staffing Executive Search",
      provider: {
        "@type": "Organization",
        name: "25th & Staffing",
      },
      serviceType: "Retained Executive Search",
      description:
        "Executive search solutions rooted in results. Adaptive, research-driven leadership recruitment to find and hire the perfect executive talent for your team.",
      areaServed: "Worldwide",
      offers: {
        "@type": "Offer",
        name: "Retained Executive Search",
        description:
          "Exclusive retained search mandate with search commencement deposit, shortlist delivery timeline, and placement guarantee.",
      },
    },
  },
  permanentStaffing: {
    title: "Permanent Staffing Solutions — 25th & Staffing",
    description:
      "Permanent and contract staffing solutions. Comprehensive recruitment services tailored to your company's culture, values, and workforce requirements.",
    schema: {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: "25th & Staffing Permanent Staffing",
      provider: {
        "@type": "Organization",
        name: "25th & Staffing",
      },
      serviceType: "Permanent Staffing",
      description:
        "Permanent and contract staffing solutions. Comprehensive recruitment services tailored to your company's culture, values, and workforce requirements.",
      areaServed: "Worldwide",
    },
  },
  recruitmentServices: {
    title: "Services — 25th & Staffing",
    description:
      "Unrivaled value through tailored staffing solutions. Flexible recruitment packages designed to meet your hiring needs from entry-level to executive roles.",
    schema: {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: "25th & Staffing Recruitment Services",
      provider: {
        "@type": "Organization",
        name: "25th & Staffing",
      },
      serviceType: "Recruitment Services",
      description:
        "Unrivaled value through tailored staffing solutions. Flexible recruitment packages designed to meet your hiring needs from entry-level to executive roles.",
      areaServed: "Worldwide",
    },
  },
  direct25th: {
    title: "25th Direct — AI Candidate Sourcing and Screening",
    description:
      "Standard Plus recruitment package for entry to mid-level roles. Basic recruitment services delivering qualified talent with a 30-day placement guarantee.",
    schema: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "25th Direct",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      provider: {
        "@type": "Organization",
        name: "25th & Staffing",
      },
      description:
        "Standard Plus recruitment package for entry to mid-level roles. Basic recruitment services delivering qualified talent with a 30-day placement guarantee.",
      offers: {
        "@type": "Offer",
        description: "Contact 25th & Staffing for pricing.",
      },
    },
  },
  aiRecruiter: {
    title: "AI Recruiter — Structured AI Interviews at Scale",
    description:
      "25th and Staffing AI Recruiter. The future of intelligent recruitment is here—transform your hiring process with cutting-edge AI technology.",
    schema: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "AI Recruiter by 25th & Staffing",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      provider: {
        "@type": "Organization",
        name: "25th & Staffing",
      },
      description:
        "25th and Staffing AI Recruiter. The future of intelligent recruitment is here—transform your hiring process with cutting-edge AI technology.",
      featureList: [
        "Structured AI interviews",
        "Behavioural signal tracking",
        "Automated scheduling",
        "Candidate assessment reports",
      ],
      offers: {
        "@type": "Offer",
        description: "Contact 25th & Staffing for pricing.",
      },
    },
  },
  coreBOS: {
    title: "Core — Business Operating System for African SMEs",
    description:
      "Executive recruitment package for senior management roles. An in-depth search and assessment process backed by a 60-day placement guarantee.",
    schema: {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Core by 25th & Staffing",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web, Android",
      provider: {
        "@type": "Organization",
        name: "25th & Staffing",
      },
      description:
        "Executive recruitment package for senior management roles. An in-depth search and assessment process backed by a 60-day placement guarantee.",
      featureList: [
        "Payroll management",
        "HR framework",
        "Compliance calendar",
        "Financial reporting",
        "NAV tracking",
        "Leave management",
        "Staff records",
        "Inventory management (Core Retail)",
        "Sales tracking (Core Retail)",
      ],
      offers: [
        {
          "@type": "Offer",
          name: "Core SME",
          description:
            "For registered businesses with staff, payroll, and compliance obligations.",
        },
        {
          "@type": "Offer",
          name: "Core Retail",
          description:
            "For merchants managing inventory, staff, and sales from an Android device.",
        },
      ],
    },
  },
  suite25th: {
    title: "25th Suite — Intelligent Workforce for Enterprise",
    description:
      "VIP recruitment package for C-suite and Board-level roles. An all-inclusive, high-touch executive recruitment process with a 90-day placement guarantee.",
    schema: {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      name: "25th Suite",
      provider: {
        "@type": "Organization",
        name: "25th & Staffing",
      },
      serviceType: "Enterprise Workforce Management",
      description:
        "VIP recruitment package for C-suite and Board-level roles. An all-inclusive, high-touch executive recruitment process with a 90-day placement guarantee.",
      areaServed: "Worldwide",
      offers: {
        "@type": "Offer",
        name: "25th Suite",
        description:
          "Bespoke enterprise engagement. Onboarding by invitation following a senior leadership conversation with 25th & Staffing.",
      },
    },
  },
  // Additional pages SEO configuration
  contact: {
    title: "Contact Us — 25th & Staffing",
    description:
      "Contact Us. 25th & Staffing builds end-to-end recruitment powered by our AI Recruiter and human expertise to help you hire elite talent anywhere from Africa.",
  },
  marketplace: {
    title: "Job Marketplace & Career Opportunities — 25th & Staffing",
    description:
      "Job Marketplace. Combining AI insights with expert guidance to help you discover career opportunities, navigate hiring processes, and interview with confidence.",
  },
  teams: {
    title: "Leadership & Team — 25th & Staffing",
    description:
      "The A Team at 25th & Staffing. We build with purpose, lead with clarity, and hire for performance, delivering HR technology and digital transformation.",
  },
  origin: {
    title: "Our Story & Origin — 25th & Staffing",
    description:
      "Our Origin: From Lagos to a global force. Connecting top talent across continents and helping organizations build distributed teams with human-led, AI recruitment.",
  },
  faq: {
    title: "Frequently Asked Questions — 25th & Staffing",
    description:
      "Frequently Asked Questions. Got questions? We've got answers! Explore our FAQs on executive search, permanent staffing, recruitment packages, and AI technology.",
  },
  terms: {
    title: "Terms & Conditions — 25th & Staffing",
    description:
      "Terms and Conditions outlining the rules and regulations for the use of 25th and Staffing Limited (Nigeria) and 25th & Staffing LLC (USA) websites and services.",
  },
  policy: {
    title: "Privacy Policy — 25th & Staffing",
    description:
      "Privacy Policy for 25th & Staffing. Learn how we collect, use, and protect your personal data, candidate profiles, and privacy rights across our services.",
  },
  jobSeeker: {
    title: "Talent Network & Job Seekers — 25th & Staffing",
    description:
      "When you require career growth, think 25th. Our consultants advocate for your career to connect you with top businesses and high-impact opportunities.",
  },
  packages: {
    title: "Recruitment Packages & Offerings — 25th & Staffing",
    description:
      "Recruitment packages designed for success. Flexible hiring solutions tailored to meet business needs from entry-level positions to strategic executive hires.",
  },
};
